import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import {
  ButtonGroup,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import PlaceIcon from '@mui/icons-material/Place'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import { ControlRoomContext } from './ControlPage'
import AllIncidents from './AllIncidents'
import AmbulanceIcon from '../../../../assets/icons/Ambulance'

const priorityStyles = {
  Critical: {
    color: '#85276A',
    bg: '#FCE7F3',
    glow: 'rgba(133,39,106,0.24)',
  },
  High: {
    color: '#E32525',
    bg: '#FEE2E2',
    glow: 'rgba(227,37,37,0.24)',
  },
  Moderate: {
    color: '#F28B1F',
    bg: '#FFEDD5',
    glow: 'rgba(242,139,31,0.22)',
  },
  Low: {
    color: '#00964C',
    bg: '#DCFCE7',
    glow: 'rgba(0,150,76,0.18)',
  },
}

const typeStyles = {
  Medical: { bg: '#E6F7F9', color: '#1E7F88' },
  Transfer: { bg: '#EEF2FF', color: '#3730A3' },
  'Patient Transport': { bg: '#F3E8FF', color: '#6B21A8' },
  Fire: { bg: '#FEE2E2', color: '#991B1B' },
  Security: { bg: '#E2E8F0', color: '#334155' },
  Crowd: { bg: '#FFF7ED', color: '#9A3412' },
  Logistical: { bg: '#ECFDF5', color: '#047857' },
  Other: { bg: '#F1F5F9', color: '#475569' },
}

const priorityRank = {
  Critical: 4,
  High: 3,
  Moderate: 2,
  Low: 1,
}
const truncateDescription = (description) => {
  if (!description || description.length <= 100) return description

  return `${description.slice(0, 100).trim()}....`
}

const hasActiveResource = (incident) =>
  (incident.assignedResources ?? []).some((a) => !a.times?.clear)

export const sortActiveIncidents = (a, b) => {
  const aIsNew = a.status === 'New'
  const bIsNew = b.status === 'New'

  if (aIsNew !== bIsNew) return aIsNew ? -1 : 1

  const priorityDiff =
    (priorityRank[b.priority] ?? 0) - (priorityRank[a.priority] ?? 0)

  if (priorityDiff !== 0) return priorityDiff

  const aHasResource = hasActiveResource(a)
  const bHasResource = hasActiveResource(b)

  if (aHasResource !== bHasResource) return aHasResource ? 1 : -1

  return new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0)
}

export const sortOngoingIncidents = (a, b) => {
  const aIsCritical = a.priority === 'Critical'
  const bIsCritical = b.priority === 'Critical'

  if (aIsCritical !== bIsCritical) return aIsCritical ? -1 : 1

  const aHasResource = hasActiveResource(a)
  const bHasResource = hasActiveResource(b)

  if (aHasResource !== bHasResource) return aHasResource ? 1 : -1

  const priorityDiff =
    (priorityRank[b.priority] ?? 0) - (priorityRank[a.priority] ?? 0)

  if (priorityDiff !== 0) return priorityDiff

  return new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0)
}

export const IncidentCard = ({
  inc,
  controlRoomRef,
  showSideMeta = true,
  showResourceMeta = true,
}) => {
  const { setSelectedIncident } = useContext(ControlRoomContext)

  const priorityStyle = priorityStyles[inc.priority] || priorityStyles.Low
  const incidentType = inc.type || inc.incidentType || 'Other'
  const typeStyle = typeStyles[incidentType] || typeStyles.Other

  const activeResources =
    inc.assignedResources?.filter((a) => !a.times?.clear) ?? []

  const assignedResources =
    activeResources
      .map((a) => `${a.callSign} (${a.currentStatus})`)
      .join(', ') || '—'

  const hasNoResource = activeResources.length === 0
  const incidentRef = String(inc.incidentNumber).slice(-4)

  return (
    <Paper
      elevation={0}
      onClick={() => setSelectedIncident(inc)}
      sx={{
        p: 1,
        borderRadius: 2.25,
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        border:
          inc.status === 'New'
            ? '2px solid #ff3b3b'
            : '1px solid rgba(15,23,42,0.08)',
        animation:
          inc.status === 'New' ? 'incidentPulse 1.5s infinite' : 'none',
        boxShadow:
          inc.status === 'New'
            ? '0 0 0 rgba(255,59,59,0.7)'
            : '0 4px 12px rgba(0,0,0,0.08)',
        '@keyframes incidentPulse': {
          '0%': {
            boxShadow:
              '0 0 0 0 rgba(255,59,59,0.7), 0 0 10px rgba(255,59,59,0.35)',
          },
          '70%': {
            boxShadow:
              '0 0 0 12px rgba(255,59,59,0), 0 0 20px rgba(255,59,59,0.45)',
          },
          '100%': {
            boxShadow:
              '0 0 0 0 rgba(255,59,59,0), 0 0 10px rgba(255,59,59,0.25)',
          },
        },
        transition: 'all 0.18s ease',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 5,
          backgroundColor: priorityStyle.color,
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: priorityStyle.color,
          boxShadow: `0 16px 36px ${priorityStyle.glow}`,
        },
      }}
    >
      <Stack spacing={0.7}>
        <Stack direction="row" spacing={1.15} alignItems="stretch">
          <Box
            sx={{
              width: 62,
              minWidth: 62,
              borderRadius: 2,
              backgroundColor: priorityStyle.bg,
              color: priorityStyle.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 0.75,
              py: 0.75,
              border: `1px solid ${priorityStyle.color}22`,
            }}
          >
            <Typography
              fontWeight={950}
              fontSize="1rem"
              lineHeight={1.1}
              sx={{
                color: priorityStyle.color,
                letterSpacing: 0.3,
                textAlign: 'center',
              }}
            >
              {String(incidentRef).padStart(4, '0')}
            </Typography>
          </Box>

          <Stack spacing={0.55} flex={1} minWidth={0}>
            <Stack
              direction="row"
              gap={1}
              alignItems="flex-start"
              justifyContent="space-between"
              minWidth={0}
              // Phones: let the chip group drop under the complaint text and
              // wrap, instead of being clipped at the card edge.
              flexWrap={{ xs: 'wrap', lg: 'nowrap' }}
            >
              {showResourceMeta && (
                <Typography
                  variant="body2"
                  color="#1E293B"
                  fontWeight={800}
                  noWrap
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {inc.presentingComplaint ||
                    'No presenting complaint recorded'}
                </Typography>
              )}

              <Stack
                direction="row"
                gap={0.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{
                  flexShrink: { xs: 1, lg: 0 },
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                <Chip
                  label={incidentType}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    height: 22,
                    backgroundColor: typeStyle.bg,
                    color: typeStyle.color,
                    fontWeight: 900,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />

                <Chip
                  label={inc.priority || 'Low'}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    height: 22,
                    minWidth: 72,
                    backgroundColor: priorityStyle.color,
                    color: 'white',
                    fontWeight: inc.priority === 'Critical' ? 900 : 'inherit',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    boxShadow: `0 6px 14px ${priorityStyle.glow}`,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />

                {hasNoResource && (
                  <Chip
                    label="No Resource"
                    size="small"
                    sx={{
                      borderRadius: 999,
                      height: 22,
                      backgroundColor: '#FCE7F3',
                      color: '#85276A',
                      fontWeight: 900,
                      '& .MuiChip-label': { px: 1 },
                      animation:
                        inc.status !== 'New' && 'noResourcePulse 1.2s infinite',
                      '@keyframes noResourcePulse': {
                        '0%': {
                          boxShadow: '0 0 0 0 rgba(133,39,106,0.65)',
                          transform: 'scale(1)',
                        },
                        '70%': {
                          boxShadow: '0 0 0 8px rgba(133,39,106,0)',
                          transform: 'scale(1.04)',
                        },
                        '100%': {
                          boxShadow: '0 0 0 0 rgba(133,39,106,0)',
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  />
                )}

                {inc.status === 'New' && (
                  <Chip
                    label="New"
                    size="small"
                    sx={{
                      borderRadius: 999,
                      height: 22,
                      backgroundColor: '#FEE2E2',
                      color: '#991B1B',
                      fontWeight: 900,
                      '& .MuiChip-label': { px: 1 },
                      animation:
                        inc.status === 'New'
                          ? 'incidentPulse 1.5s infinite'
                          : 'none',
                      boxShadow:
                        inc.status === 'New'
                          ? '0 0 0 rgba(255,59,59,0.7)'
                          : '0 4px 12px rgba(0,0,0,0.08)',
                      '@keyframes incidentPulse': {
                        '0%': {
                          boxShadow:
                            '0 0 0 0 rgba(255,59,59,0.7), 0 0 5px rgba(255,59,59,0.35)',
                        },
                        '70%': {
                          boxShadow:
                            '0 0 0 6px rgba(255,59,59,0), 0 0 10px rgba(255,59,59,0.45)',
                        },
                        '100%': {
                          boxShadow:
                            '0 0 0 0 rgba(255,59,59,0), 0 0 5px rgba(255,59,59,0.25)',
                        },
                      },
                    }}
                  />
                )}
              </Stack>
            </Stack>

            {showResourceMeta ? (
              inc.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.25,
                  }}
                >
                  <strong>Description: </strong>
                  {truncateDescription(inc.description)}
                </Typography>
              )
            ) : (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                width="100%"
                minWidth={0}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    flex: '1 1 45%',
                    pr: 1.5,
                    borderRight: inc.description ? '1px solid #E2E8F0' : 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="#1E293B"
                    fontWeight={800}
                    noWrap
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {inc.presentingComplaint ||
                      'No presenting complaint recorded'}
                  </Typography>
                </Box>

                {inc.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      minWidth: 0,
                      flex: '1 1 55%',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.25,
                    }}
                  >
                    <strong>Description: </strong>
                    {truncateDescription(inc.description)}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          minWidth={0}
          sx={{ color: '#64748B', pl: 0.35 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
            <PlaceIcon sx={{ fontSize: 15, flexShrink: 0 }} />
            <Typography
              variant="caption"
              fontWeight={800}
              noWrap
              sx={{ minWidth: 0, color: '#334155' }}
            >
              {inc.location || 'Location not recorded'}
            </Typography>
          </Stack>

          {showResourceMeta && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              minWidth={0}
            >
              <AmbulanceIcon width={15} height={15} color="#64748B" />
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0 }}
              >
                {assignedResources}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

import newIncidentSound from '../../../../assets/sounds/newIncident.mp3'
import newCriticalIncidentSound from '../../../../assets/sounds/newCriticalIncident.mp3'
import { UserContext } from '../../../../App'
const normalIncidentAudio = new Audio(newIncidentSound)
const criticalIncidentAudio = new Audio(newCriticalIncidentSound)

const ActiveIncidents = ({
  incidents = [],
  controlRoomRef,
  controlRoomID,
  gridProps = {},
  compact = false,
  title = 'Ongoing Incidents',
  subtitle,
  emptyText = 'No ongoing incidents.',
  sortIncidents = sortOngoingIncidents,
}) => {
  const { setOpenNewIncident, canDispatch = true } =
    useContext(ControlRoomContext)
  const [openAllIncidents, setOpenAllIncidents] = useState(false)
  const previousIncidentIds = useRef([])

  const { user } = useContext(UserContext)
  const sortedIncidents = useMemo(
    () => [...incidents].sort(sortIncidents),
    [incidents, sortIncidents],
  )

  useEffect(() => {
    if (!incidents?.length) return

    const currentIds = incidents.map((x) => x.id)

    const newIncidents = incidents.filter(
      (x) => !previousIncidentIds.current.includes(x.id),
    )

    // Ignore first load
    if (previousIncidentIds.current.length > 0) {
      const incidentToAlert = newIncidents.find((incident) => {
        const createdById =
          typeof incident.createdBy === 'object'
            ? incident.createdBy?._id
            : incident.createdBy

        return String(createdById) !== String(user?._id)
      })

      if (incidentToAlert) {
        const audio =
          incidentToAlert.priority === 'Critical'
            ? criticalIncidentAudio
            : normalIncidentAudio

        audio.currentTime = 0
        audio.volume = 0.6

        audio.play().catch(() => {
          // Browser blocked autoplay
        })
      }
    }

    previousIncidentIds.current = currentIds
  }, [incidents, user?._id])

  return (
    <Grid item xs={12} lg={6} {...gridProps}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #E32525 170%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
              }}
            >
              <ReportProblemIcon />
            </Box>

            <Stack spacing={0}>
              <Typography fontWeight={900} color="white">
                {title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.72 }}>
                {subtitle || `${incidents.length} with resources assigned`}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton
              size="small"
              onClick={() => setOpenAllIncidents(true)}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.08)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
              }}
            >
              <ManageSearchIcon />
            </IconButton>

            {canDispatch && (
              <IconButton
                size="small"
                onClick={() => setOpenNewIncident(true)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                }}
              >
                <AddIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>

        <Stack
          spacing={1.25}
          sx={{
            p: 2,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {incidents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {emptyText}
            </Typography>
          ) : (
            sortedIncidents.map((inc) => (
              <IncidentCard
                inc={inc}
                controlRoomRef={controlRoomRef}
                key={inc.id}
              />
            ))
          )}
        </Stack>
      </Paper>

      <AllIncidents
        open={openAllIncidents}
        onClose={() => setOpenAllIncidents(false)}
        controlRoomID={controlRoomID}
        controlRoomRef={controlRoomRef}
      />
    </Grid>
  )
}

export default ActiveIncidents
